import { History, ScanLine, ShieldAlert, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Spinner } from '@/shared/ui/spinner';
import { LeadAvatar } from '@/shared/components/lead-avatar';
import { isCardInstalled, quantityLabel } from '@/shared/lib/passbook-utils';
import { currency, decimal, formatDate , type CurrencyCode} from '@/shared/lib/format';
import { formatMovementDelta, movementActionLabel } from '@/features/leads/model/lead-logs';
import type { Lead, Passbook, PointLog } from '@/shared/types';
import { detectOperatorProgram } from '../model/operator-rules';

interface OperatorCustomerCardProps {
  lead: Lead;
  passbook: Passbook;
  idPassbook: string;
  logs: PointLog[];
  logsLoading?: boolean;
  currencyCode: CurrencyCode;
}

export function OperatorCustomerCard({
  lead,
  passbook,
  idPassbook,
  logs,
  logsLoading,
  currencyCode,
}: OperatorCustomerCardProps) {
  const label = quantityLabel(passbook);
  const balance = lead.total_points ?? 0;
  const program = detectOperatorProgram(passbook);
  const moneyLike = program === 'cashback' || program === 'giftcard' || program === 'balance';
  const firstName = (lead.profile?.name ?? 'cliente').trim().split(/\s+/)[0];
  const recent = logs.slice(0, 5);
  const blocked = lead.status === 'blocked';

  return (
    <div className="flex flex-col gap-3">
      {blocked ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3"
        >
          <ShieldAlert className="mt-0.5 size-5 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-semibold text-destructive">Cliente bloqueado</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {lead.blockInfo?.reason
                ? lead.blockInfo.reason
                : 'Não é possível pontuar nem resgatar.'}
            </p>
          </div>
        </div>
      ) : null}
      <div className="flex shrink-0 items-start gap-3">
        <LeadAvatar lead={lead} className="size-11" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                Cliente identificado
              </p>
              <h2 className="truncate text-xl font-semibold tracking-tight">Olá, {firstName}!</h2>
              <p className="truncate text-sm text-muted-foreground">
                {passbook.fieldUniqueIdLabel ?? 'CPF'}: {lead.profile?.uniqueId}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Badge variant={isCardInstalled(lead) ? 'success' : 'secondary'}>
                {isCardInstalled(lead) ? 'Instalado' : 'Não instalado'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-thin flex shrink-0 items-center justify-between gap-3 rounded-2xl px-4 py-3">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Saldo · {label}
          </p>
          <p className="mt-0.5 text-2xl font-bold tabular-nums tracking-tight text-primary sm:text-3xl">
            {moneyLike ? currency(balance, currencyCode) : decimal(balance)}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex shrink-0 items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Últimas movimentações
          </p>
          {lead._id ? (
            <Link
              to={`/loja/${idPassbook}/leads/${lead._id}?tab=movimentos`}
              className="inline-flex min-h-8 items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <History className="size-3.5" />
              Histórico
            </Link>
          ) : null}
        </div>
        {logsLoading ? (
          <div className="flex justify-center py-2">
            <Spinner />
          </div>
        ) : recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma movimentação recente.</p>
        ) : (
          // Sem overflow próprio: o Wizard já tem scrollableBody. Scroll aninhado
          // aqui prendia a roda do mouse numa faixa mínima e dificultava chegar ao Próximo.
          <ul className="space-y-1">
            {recent.map((log) => (
              <li
                key={log._id ?? `${log.date}-${log.value}-${log.action}`}
                className="glass-thin flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{movementActionLabel(log, passbook)}</p>
                  <p className="text-[11px] text-muted-foreground">{formatDate(log.date)}</p>
                </div>
                <span className="shrink-0 tabular-nums text-sm">
                  {formatMovementDelta(log, passbook)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function OperatorIdentifyEmpty({
  uniqueIdLabel,
  onScan,
}: {
  uniqueIdLabel: string;
  onScan: () => void;
}) {
  return (
    <div className="flex shrink-0 flex-col items-center px-2 pb-2 pt-6 text-center sm:pt-8">
      <div className="grid size-12 place-items-center rounded-2xl bg-primary/12 text-primary">
        <UserRound className="size-6" />
      </div>
      <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
        Identificação do cliente
      </p>
      <h2 className="mt-1.5 text-xl font-semibold tracking-tight sm:text-2xl">
        Informe o {uniqueIdLabel}
      </h2>
      <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">
        Digite acima ou escaneie o QR do wallet para montar a conta.
      </p>
      <Button type="button" variant="secondary" className="mt-4 h-10 gap-2" onClick={onScan}>
        <ScanLine className="size-4" />
        Escanear QR do cliente
      </Button>
    </div>
  );
}
